<script lang="ts">
    import {getLocalization} from 'src/core';
    import type {DirectoryNode} from 'src/typings';
    import {createEventDispatcher} from 'svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import MvContextMenuWrapper from 'src/features/media-viewer/components/mv-file-explorer/mv-context-menu/MvContextMenuWrapper.svelte';
    import MvContextMenuDivider from 'src/features/media-viewer/components/mv-file-explorer/mv-context-menu/MvContextMenuDivider.svelte';
    import MvContextMenuTitle from 'src/features/media-viewer/components/mv-file-explorer/mv-context-menu/MvContextMenuTitle.svelte';
    import KpContextMenuButton from 'shared/components/kp-context-menu/KpContextMenuButton.svelte';

    export let directoryNode: DirectoryNode;
    export let selectedDirectory: DirectoryNode | null;
    export let level: number;

    const localize = getLocalization();
    const dispatch = createEventDispatcher<{ 'directory-select': DirectoryNode | null }>();

    const directoryContents = directoryNode.children;
    $: directoryEmpty = directoryContents.length === 0;
    $: directorySelected = selectedDirectory?.id === directoryNode.id;

    const handleFolderClick = () => {
        if(directorySelected) {
            dispatch('directory-select', null);
            return;
        }

        dispatch('directory-select', directoryNode);
    }
</script>

<div id="overview-directory-{directoryNode.id}"
     class="mv-overview-directory"
     role="treeitem"
     aria-level={level}
     aria-selected="false"
     aria-expanded="true">

    <MvContextMenuWrapper id="tree-view-directory-{directoryNode.id}"
                          on:click={handleFolderClick}
                          let:open>

        <svelte:fragment slot="context-content">
            <MvContextMenuTitle>
                {localize(/* @kp-localization mediaviewer.fileExplorer.DirectoryContextLabel */ 'mediaviewer.fileExplorer.DirectoryContextLabel')}
            </MvContextMenuTitle>

            <MvContextMenuDivider/>

            <KpContextMenuButton icon={directorySelected ? 'cross-circle' : 'checkbox'}
                                 buttonStyle={directorySelected ? 'brand-orange' : 'default'}
                                 on:click={handleFolderClick}>

                {directorySelected ? 'Zrušit výběr' : 'Vybrat'}
            </KpContextMenuButton>
        </svelte:fragment>

        <div class="directory-name-row"
             class:context-menu-opened={open}
             class:selected={directorySelected}>

            <UIcon icon="folder"/>
            <span class="text-ellipsis">{directoryNode.text}</span>

            <span class="full-width-divider"></span>

            {#if !directoryEmpty}
                <span class="unset-style open-indicator-container">
                    <UIcon icon="angle-small-right"/>
                </span>
            {:else}
                <span class="text-muted empty-label">
                    {localize(/* @kp-localization mediaviewer.fileExplorer.DirectoryEmptyShortLabel */ 'mediaviewer.fileExplorer.DirectoryEmptyShortLabel')}
                </span>
            {/if}
        </div>
    </MvContextMenuWrapper>

    {#if directoryContents.length > 0}
        <ul class="directory-list" role="group">
            {#each directoryContents as directoryContent(directoryContent.id)}
                <svelte:self directoryNode={directoryContent}
                             level={level + 1}
                             {selectedDirectory}
                             on:directory-select/>
            {/each}
        </ul>
    {/if}
</div>

<style lang="less">
    @import (reference) "src/features/media-viewer/media-viewer.variables.less";

    @vertical-row-spacing: 4px;

    .mv-overview-directory {
        display: flex;
        flex-direction: column;

        .directory-name-row {
            text-align: start;
            position: relative;
            display: flex;
            outline: none;
            cursor: pointer;
            align-items: center;
            gap: @spacing-sm;
            padding: @vertical-row-spacing @spacing-s @vertical-row-spacing @spacing-base;
            border-radius: @border-radius-default;
            border: 1px solid transparent;
            transition: border-color 0.3s ease-in-out, background-color 0.3s ease-in-out;

            &:hover,
            &.context-menu-opened {
                border-color: var(--accent-blue);
            }

            &.selected {
                background-color: var(--viewer-blue-highlight-transparent);
                border-color: var(--accent-blue);
            }

            .open-indicator-container {
                transform: rotate(90deg);
                opacity: 1;
                transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
            }

            .empty-label {
                font-size: @font-size-small;
            }

            .full-width-divider {
                margin-left: auto;
            }
        }

        .directory-list {
            margin-top: @spacing-base;
            margin-left: 14px;
            border-left: 1px solid var(--viewer-bold-border);
            display: flex;
            flex-direction: column;
            gap: @spacing-base;
            padding-left: @spacing-base;
            transition: border-left-color 0.15s ease-in-out;
        }
    }
</style>